'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth, useUser } from '@clerk/nextjs';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';

import { Popup } from '@shared/ui/components';
import AvatarIcon from '@public/icons/avatar.svg';
import AvatarSquareIcon from '@public/icons/avatar-square.svg';
import ArrowIcon from '@public/icons/arrow-left-with-tail.svg';
import ExitIcon from '@public/icons/exit.svg';
import FullLogo from '@public/logo-full.svg';

import { routesWithBackButton } from '../../config/routes';

import styles from './Header.module.scss';

const Header = () => {
  const t = useTranslations('navigation');
  const pathname = usePathname();
  const router = useRouter();

  const [isNavigationOpen, setNavigationOpen] = useState<boolean>(false);

  const [, routeName] = routesWithBackButton.find(([r]) => r.test(pathname)) ?? [];

  const { signOut } = useAuth();
  const { user } = useUser();

  const handleSignOut = () => {
    signOut().then(() => {
      router.push('/sign-in');

      setNavigationOpen(false);
    });
  };

  return (
    <header className={styles.header}>
      {routeName ? (
        <Image
          src={ArrowIcon}
          className={styles.backButton}
          alt="Arrow"
          draggable="false"
          onClick={router.back}
        />
      ) : (
        <Link href="/modules" className={styles.logo}>
          <Image src={FullLogo} className={styles.logo_icon} alt="Logo" draggable="false" />
          <div className={styles.logo_seporator} />
          <div>
            <div>Quantum Computing</div>
            <div>Educational Program</div>
          </div>
        </Link>
      )}

      {!!routeName && <div className={styles.title}>{t(routeName)}</div>}

      {user && (
        <Popup
          isOpen={isNavigationOpen}
          anchor={
            <>
              {!!user && (
                <div
                  className={clsx(
                    styles.account_info,
                    !!routeName && styles.account_info_alternative,
                  )}
                >
                  {user.firstName && (
                    <div className={styles.account_info_name}>{user.firstName}</div>
                  )}

                  <div className={styles.account_info_email}>
                    {user.primaryEmailAddress?.emailAddress}
                  </div>
                </div>
              )}

              <div className={styles.profile}>
                <Image src={AvatarIcon} alt="Avatar" draggable={false} />
              </div>
            </>
          }
          positionX="right-in"
          anchorClassName={styles.account}
          popupClassName={styles.navigation}
          setOpen={setNavigationOpen}
        >
          <Link
            href="/profile"
            className={styles.navigation_option}
            onClick={() => setNavigationOpen(false)}
          >
            <Image src={AvatarSquareIcon} alt="Avatar" draggable={false} />
            <div>{t('profile')}</div>
          </Link>

          <div
            className={clsx(styles.navigation_option, styles.navigation_option_error)}
            onClick={handleSignOut}
          >
            <Image src={ExitIcon} alt="Exit" draggable={false} />
            <div>{t('signOut')}</div>
          </div>
        </Popup>
      )}
    </header>
  );
};

export default Header;
